import { Link } from "wouter";
import { Bell, Pill } from "lucide-react";
import type { StatTileId } from "./StatRow";
import { useNotifications } from "../../lib/api/useNotifications";
import { useDashboardExpiringMedications } from "../../lib/api/useDashboardOverview";
import { DialogShell } from "../ui/DialogShell";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { cn } from "../../lib/cn";

/** The tiles this dialog explains. Shared care links has its own popup (ActiveLinksDialog). */
export type StatDetailTile = Exclude<StatTileId, "links">;

/**
 * Detail behind a dashboard stat tile.
 *
 * One dialog, one panel per tile, chosen by which tile was clicked. Alerts
 * reads data the dashboard has already loaded; Expiring meds (shown only while
 * HEALTH_REMINDERS_ENABLED is on) fetches on open, so nobody pays for detail
 * they never look at.
 *
 * Every panel renders an explicit empty state rather than nothing — a tile
 * showing 0 is still clickable, and an empty dialog would read as a failure.
 */
export function StatDetailDialog({
  tile,
  onClose,
}: {
  tile: StatDetailTile | null;
  onClose: () => void;
}) {
  const meta = tile ? PANELS[tile] : null;

  return (
    <DialogShell
      open={tile !== null}
      onOpenChange={(open) => !open && onClose()}
      title={meta?.title ?? ""}
      description={meta?.description}
      size="xl"
    >
      <div className="mt-6">
        {tile === "alerts" && <AlertsPanel onNavigate={onClose} />}
        {tile === "medications" && <ExpiringMedicationsPanel open />}
      </div>
    </DialogShell>
  );
}

const PANELS: Record<StatDetailTile, { title: string; description: string }> = {
  alerts: { title: "Alerts", description: "Things that may need your attention." },
  medications: {
    title: "Expiring medications",
    description: "Medications reaching their expiry date soon.",
  },
};

/** Shared list chrome. max-h + overflow keeps the dialog title in place. */
function List({ children }: { children: React.ReactNode }) {
  return (
    <ul className="max-h-80 space-y-2 overflow-y-auto p-0.5">{children}</ul>
  );
}

/** Open, centred "nothing here" (STYLE.md): a round pebble, no dashed box. */
function Empty({ icon: Icon, children }: { icon: typeof Bell; children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center px-4 py-6 text-center">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-emerald-100 to-sky-100 text-emerald-700 shadow-soft ring-8 ring-white/60">
        <Icon className="h-6 w-6" aria-hidden />
      </span>
      <p className="mt-3 text-sm text-neutral-600">{children}</p>
    </div>
  );
}

function Loading() {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-8 text-sm text-neutral-600">
      <Spinner className="h-4 w-4" /> Loading…
    </div>
  );
}

function Failed() {
  return (
    <p className="rounded-xl bg-red-50 px-4 py-6 text-center text-sm text-red-700">
      Couldn&apos;t load this right now. Close and try again.
    </p>
  );
}

// Soft rows on the dialog's sheet (a ring, not a shadow: the list scrolls and would clip one).
const rowClass = "flex items-center gap-3 rounded-2xl bg-white px-4 py-3 text-sm ring-1 ring-sky-100";

function AlertsPanel({ onNavigate }: { onNavigate: () => void }) {
  const { data, isLoading, error } = useNotifications();
  if (isLoading) return <Loading />;
  if (error) return <Failed />;
  const unread = (data?.items ?? []).filter((n) => n.readAt === null);
  if (unread.length === 0) {
    return <Empty icon={Bell}>Nothing needs your attention right now.</Empty>;
  }
  return (
    <>
      <List>
        {unread.slice(0, 6).map((n) => (
          <li key={n.id} className={cn(rowClass, "items-start")}>
            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-rose-500" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-neutral-900">{n.title}</p>
              <p className="text-xs text-neutral-600">{n.body}</p>
            </div>
          </li>
        ))}
      </List>
      <div className="mt-4 flex justify-end">
        <Button asChild variant="secondary" size="sm">
          <Link href="/notifications" onClick={onNavigate}>
            View all alerts
          </Link>
        </Button>
      </div>
    </>
  );
}

function ExpiringMedicationsPanel({ open }: { open: boolean }) {
  const { data: meds, isLoading, error } = useDashboardExpiringMedications(open);
  if (isLoading) return <Loading />;
  if (error) return <Failed />;
  if (!meds || meds.length === 0) {
    return <Empty icon={Pill}>No medication is expiring in the next 30 days.</Empty>;
  }
  return (
    <List>
      {meds.map((m) => (
        <li key={m.id} className={rowClass}>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-neutral-900">{m.name}</p>
            <p className="truncate text-xs text-neutral-600">
              {m.childName}
              {m.dosage ? ` · ${m.dosage}` : ""}
            </p>
          </div>
          <span
            className={cn(
              "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
              m.daysUntilExpiry <= 7
                ? "bg-red-100 text-red-800"
                : "bg-amber-100 text-amber-800",
            )}
          >
            {m.daysUntilExpiry <= 0 ? "Today" : `in ${m.daysUntilExpiry}d`}
          </span>
        </li>
      ))}
    </List>
  );
}
